import * as React from 'react';
import { useEffect, useState, useCallback, useRef } from 'react';
import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import Card from '@mui/joy/Card';
import CardContent from '@mui/joy/CardContent';
import Button from '@mui/joy/Button';
import Input from '@mui/joy/Input';
import Textarea from '@mui/joy/Textarea';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import FormHelperText from '@mui/joy/FormHelperText';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import Table from '@mui/joy/Table';
import Sheet from '@mui/joy/Sheet';
import Chip from '@mui/joy/Chip';
import CircularProgress from '@mui/joy/CircularProgress';
import Divider from '@mui/joy/Divider';
import Switch from '@mui/joy/Switch';
import Alert from '@mui/joy/Alert';
import Grid from '@mui/joy/Grid';
import Tooltip from '@mui/joy/Tooltip';
import IconButton from '@mui/joy/IconButton';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import GeneralApiService from "../../axios/Axios";
import { GetAllFranchises } from "../../axios/Axios";
import { toast } from 'react-toastify';

// Types
interface SampleLead {
    id: number;
    customer_name: string;
    customer_phone: string;
    services: string;
    total_price: number;
    created_at: string;
}

interface PreviewResponse {
    count: number;
    sample_leads: SampleLead[];
}

interface FranchiseOption {
    id: number;
    location: string;
    sms_campaign_enabled?: boolean;
}

interface Campaign {
    id: number;
    created_at: string;
    created_by_username: string;
    franchise_location: string;
    message_template: string;
    recipients_count: number;
    sent_count: number;
    failed_count: number;
    status: 'pending' | 'sending' | 'completed' | 'failed';
    filters_used: {
        last_x_leads: number;
        cooldown_days: number;
        new_customers_only?: boolean;
    };
}

// Available variables for message template
const TEMPLATE_VARIABLES = [
    { label: 'First Name', value: '{{first_name}}', description: 'Customer\'s first name' },
    { label: 'Full Name', value: '{{customer_name}}', description: 'Customer\'s full name' },
    { label: 'Total Price', value: '{{total_price}}', description: 'Quote total (e.g., £89.00)' },
    { label: 'Services', value: '{{services}}', description: 'List of services quoted' },
    { label: 'Postcode', value: '{{postcode}}', description: 'Customer\'s postcode' },
    { label: 'Franchise Phone', value: '{{franchise_phone}}', description: 'Franchise phone number' },
    { label: 'Franchise Owner', value: '{{franchise_owner}}', description: 'Franchise owner name' },
    { label: 'Location', value: '{{location}}', description: 'Customer\'s town (e.g., Swansea)' },
];

// Status chip component
const StatusChip = ({ status }: { status: Campaign['status'] }) => {
    const config = {
        pending: { color: 'warning' as const, icon: <WarningIcon sx={{ fontSize: 14 }} /> },
        sending: { color: 'primary' as const, icon: <CircularProgress size="sm" sx={{ '--CircularProgress-size': '14px' }} /> },
        completed: { color: 'success' as const, icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> },
        failed: { color: 'danger' as const, icon: <ErrorIcon sx={{ fontSize: 14 }} /> },
    };
    const { color, icon } = config[status] || config.pending;

    return (
        <Chip
            variant="soft"
            color={color}
            size="sm"
            startDecorator={icon}
        >
            {status.charAt(0).toUpperCase() + status.slice(1)}
        </Chip>
    );
};

export default function BulkSMS() {
    // Message composer state
    const [message, setMessage] = useState('');
    const [lastXLeads, setLastXLeads] = useState(100);
    const [cooldownDays, setCooldownDays] = useState(7);
    const [newCustomersOnly, setNewCustomersOnly] = useState(false);
    const [selectedFranchiseId, setSelectedFranchiseId] = useState<string>('');
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    // Franchise filter state
    const [franchises, setFranchises] = useState<FranchiseOption[]>([]);
    const [loadingFranchises, setLoadingFranchises] = useState(true);

    // Preview state
    const [previewCount, setPreviewCount] = useState<number | null>(null);
    const [sampleLeads, setSampleLeads] = useState<SampleLead[]>([]);
    const [loadingPreview, setLoadingPreview] = useState(false);

    // Campaigns state
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [loadingCampaigns, setLoadingCampaigns] = useState(true);

    // Send state
    const [sending, setSending] = useState(false);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);

    // Test SMS state
    const [testLeadId, setTestLeadId] = useState('');
    const [sendingTest, setSendingTest] = useState(false);

    // Calculate character count (SMS segments)
    const characterCount = message.length;
    const smsSegments = Math.ceil(characterCount / 160) || 1;

    // Load campaigns on mount
    useEffect(() => {
        loadFranchises();
        loadCampaigns();
    }, []);

    const loadFranchises = async () => {
        setLoadingFranchises(true);
        try {
            const response = await GetAllFranchises();
            setFranchises(response.data || []);
        } catch (error) {
            console.error('Failed to load franchises:', error);
            toast.error('Failed to load franchises');
        } finally {
            setLoadingFranchises(false);
        }
    };

    const loadCampaigns = async () => {
        setLoadingCampaigns(true);
        try {
            const data = await GeneralApiService.getBulkSMSCampaigns();
            setCampaigns(data.results || []);
        } catch (error) {
            console.error('Failed to load campaigns:', error);
            toast.error('Failed to load campaign history');
        } finally {
            setLoadingCampaigns(false);
        }
    };

    // Fetch preview with debounce
    const fetchPreview = useCallback(async () => {
        setLoadingPreview(true);
        try {
            const data: PreviewResponse = await GeneralApiService.getBulkSMSPreview(
                lastXLeads,
                cooldownDays,
                newCustomersOnly,
                selectedFranchiseId || null
            );
            setPreviewCount(data.count);
            setSampleLeads(data.sample_leads || []);
        } catch (error) {
            console.error('Failed to fetch preview:', error);
            toast.error('Failed to load recipient preview');
            setPreviewCount(null);
            setSampleLeads([]);
        } finally {
            setLoadingPreview(false);
        }
    }, [lastXLeads, cooldownDays, newCustomersOnly, selectedFranchiseId]);

    // Load preview when filters change
    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            fetchPreview();
        }, 500);
        return () => clearTimeout(debounceTimer);
    }, [fetchPreview]);

    // Insert variable at cursor position
    const insertVariable = (variable: string) => {
        const textarea = textareaRef.current;
        if (textarea) {
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            const newMessage = message.substring(0, start) + variable + message.substring(end);
            setMessage(newMessage);
            // Set cursor position after inserted variable
            setTimeout(() => {
                textarea.focus();
                textarea.setSelectionRange(start + variable.length, start + variable.length);
            }, 0);
        } else {
            setMessage(message + variable);
        }
    };

    // Handle send
    const handleSend = async () => {
        if (!message.trim()) {
            toast.error('Please enter a message');
            return;
        }
        if (previewCount === 0) {
            toast.error('No recipients match your criteria');
            return;
        }
        setConfirmModalOpen(true);
    };

    const confirmSend = async () => {
        setConfirmModalOpen(false);
        setSending(true);
        try {
            const response = await GeneralApiService.sendBulkSMS(
                message,
                lastXLeads,
                cooldownDays,
                newCustomersOnly,
                selectedFranchiseId || null
            );
            toast.success(`Campaign started! Sending to ${response.recipients_count} recipients.`);
            setMessage('');
            // Refresh campaigns and preview
            loadCampaigns();
            fetchPreview();
        } catch (error: any) {
            console.error('Failed to send bulk SMS:', error);
            toast.error(error.response?.data?.error || 'Failed to send campaign');
        } finally {
            setSending(false);
        }
    };

    // Handle test SMS
    const handleSendTest = async () => {
        if (!message.trim()) {
            toast.error('Please enter a message');
            return;
        }
        if (!testLeadId.trim()) {
            toast.error('Please enter a Lead ID');
            return;
        }
        
        setSendingTest(true);
        try {
            const response = await GeneralApiService.sendTestBulkSMS(message, parseInt(testLeadId));
            toast.success(`Test SMS sent to ${response.customer_name} (${response.phone})`);
        } catch (error: any) {
            console.error('Failed to send test SMS:', error);
            toast.error(error.response?.data?.error || 'Failed to send test SMS');
        } finally {
            setSendingTest(false);
        }
    };

    // Format date
    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const selectedFranchise = franchises.find(f => String(f.id) === selectedFranchiseId);
    const disabledFranchisesCount = franchises.filter(f => f.sms_campaign_enabled === false).length;

    return (
        <Box>
            <Grid container spacing={2}>
                {/* Left Column - Filters & Preview */}
                <Grid xs={12} md={4}>
                    <Card variant="outlined" sx={{ mb: 2 }}>
                        <CardContent>
                            <Typography level="title-md" sx={{ mb: 2 }}>
                                Target Filters
                            </Typography>

                            <FormControl sx={{ mb: 2 }}>
                                <FormLabel>Franchise</FormLabel>
                                <Select
                                    value={selectedFranchiseId}
                                    onChange={(_, value) => setSelectedFranchiseId(value || '')}
                                    disabled={loadingFranchises}
                                >
                                    <Option value="">All Franchises</Option>
                                    {franchises.map((franchise) => (
                                        <Option 
                                            key={franchise.id} 
                                            value={String(franchise.id)}
                                            disabled={franchise.sms_campaign_enabled === false}
                                        >
                                            {franchise.location} {franchise.sms_campaign_enabled === false ? '(SMS Disabled)' : ''}
                                        </Option>
                                    ))}
                                </Select>
                                <FormHelperText>
                                    {selectedFranchise && selectedFranchise.sms_campaign_enabled === false
                                        ? 'This franchise has SMS campaigns disabled. No customers will be messaged.'
                                        : disabledFranchisesCount > 0 && !selectedFranchiseId
                                            ? `Customers from ${disabledFranchisesCount} franchise${disabledFranchisesCount > 1 ? 's' : ''} with SMS campaigns disabled are excluded.`
                                            : 'Limit this campaign to customers assigned to one franchise'}
                                </FormHelperText>
                                {selectedFranchise && selectedFranchise.sms_campaign_enabled === false && (
                                    <Alert color="warning" size="sm" sx={{ mt: 1 }}>
                                        SMS campaigns are disabled for <strong>{selectedFranchise.location}</strong>. Customers assigned to this franchise will not receive bulk SMS.
                                    </Alert>
                                )}
                            </FormControl>

                            <FormControl sx={{ mb: 2 }}>
                                <FormLabel>Last X Leads</FormLabel>
                                <Input
                                    type="number"
                                    value={lastXLeads}
                                    onChange={(e) => setLastXLeads(Math.max(1, parseInt(e.target.value) || 1))}
                                    slotProps={{ input: { min: 1, max: 10000 } }}
                                />
                                <FormHelperText>
                                    Target the most recent leads (ordered by date)
                                </FormHelperText>
                            </FormControl>

                            <FormControl>
                                <FormLabel>Cooldown Period (days)</FormLabel>
                                <Input
                                    type="number"
                                    value={cooldownDays}
                                    onChange={(e) => setCooldownDays(Math.max(0, parseInt(e.target.value) || 0))}
                                    slotProps={{ input: { min: 0, max: 365 } }}
                                />
                                <FormHelperText>
                                    Skip customers who received a bulk SMS within this period
                                </FormHelperText>
                            </FormControl>

                            <Divider sx={{ my: 2 }} />

                            <FormControl orientation="horizontal" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box>
                                    <FormLabel>New Customers Only</FormLabel>
                                    <FormHelperText sx={{ mt: 0 }}>
                                        Only message customers created after 23rd Feb 2026
                                    </FormHelperText>
                                </Box>
                                <Switch
                                    checked={newCustomersOnly}
                                    onChange={(e) => setNewCustomersOnly(e.target.checked)}
                                    color={newCustomersOnly ? 'success' : 'neutral'}
                                />
                            </FormControl>
                        </CardContent>
                    </Card>

                    <Card variant="outlined">
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                <Typography level="title-md">
                                    Preview
                                </Typography>
                                <IconButton
                                    size="sm"
                                    variant="plain"
                                    onClick={fetchPreview}
                                    disabled={loadingPreview}
                                >
                                    <RefreshIcon />
                                </IconButton>
                            </Box>

                            {loadingPreview ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                    <CircularProgress size="md" />
                                </Box>
                            ) : (
                                <>
                                    <Box sx={{ 
                                        textAlign: 'center', 
                                        py: 2, 
                                        px: 3, 
                                        bgcolor: 'background.level1', 
                                        borderRadius: 'md',
                                        mb: 2 
                                    }}>
                                        <Typography level="h2" sx={{ color: 'primary.500' }}>
                                            {previewCount?.toLocaleString() || 0}
                                        </Typography>
                                        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                                            recipients will receive this SMS
                                        </Typography>
                                    </Box>

                                    {sampleLeads.length > 0 && (
                                        <>
                                            <Typography level="body-sm" sx={{ fontWeight: 'md', mb: 1 }}>
                                                Sample Recipients:
                                            </Typography>
                                            <Box sx={{ fontSize: 'xs' }}>
                                                {sampleLeads.map((lead, index) => (
                                                    <Box 
                                                        key={lead.id} 
                                                        sx={{ 
                                                            py: 0.75, 
                                                            borderBottom: index < sampleLeads.length - 1 ? '1px solid' : 'none',
                                                            borderColor: 'divider'
                                                        }}
                                                    >
                                                        <Typography level="body-xs" sx={{ fontWeight: 'md' }}>
                                                            {lead.customer_name} ({lead.customer_phone})
                                                        </Typography>
                                                        <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                                            {lead.services} - £{lead.total_price}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </>
                                    )}

                                    {previewCount === 0 && (
                                        <Alert color="warning" size="sm">
                                            No leads match your current filters. Try adjusting the cooldown period or lead count.
                                        </Alert>
                                    )}
                                </>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Right Column - Message Composer */}
                <Grid xs={12} md={8}>
                    <Card variant="outlined" sx={{ mb: 2 }}>
                        <CardContent>
                            <Typography level="title-md" sx={{ mb: 2 }}>
                                Message Composer
                            </Typography>

                            <Alert 
                                color="neutral" 
                                size="sm" 
                                sx={{ mb: 2 }}
                                startDecorator={<InfoOutlinedIcon />}
                            >
                                Use variables to personalize your message. Click a variable below to insert it.
                            </Alert>

                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                {TEMPLATE_VARIABLES.map((variable) => (
                                    <Tooltip key={variable.value} title={variable.description} size="sm">
                                        <Chip
                                            variant="soft"
                                            color="primary"
                                            size="sm"
                                            onClick={() => insertVariable(variable.value)}
                                            sx={{ cursor: 'pointer' }}
                                        >
                                            {variable.label}
                                        </Chip>
                                    </Tooltip>
                                ))}
                            </Box>

                            <FormControl sx={{ mb: 2 }}>
                                <Textarea
                                    slotProps={{
                                        textarea: {
                                            ref: textareaRef,
                                        },
                                    }}
                                    placeholder="Hi {{first_name}}, we noticed you were interested in {{services}} for {{total_price}}. Reply YES to book or call us at {{franchise_phone}}!"
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    minRows={6}
                                    maxRows={10}
                                    sx={{ fontFamily: 'monospace', fontSize: 'sm' }}
                                />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                    <FormHelperText>
                                        {characterCount} characters ({smsSegments} SMS segment{smsSegments > 1 ? 's' : ''})
                                    </FormHelperText>
                                    {characterCount > 160 && (
                                        <Typography level="body-xs" color="warning">
                                            Long messages may be split into multiple SMS
                                        </Typography>
                                    )}
                                </Box>
                            </FormControl>

                            <Divider sx={{ my: 2 }} />

                            {/* Test SMS Section */}
                            <Box sx={{ mb: 2, p: 2, bgcolor: 'background.level1', borderRadius: 'md' }}>
                                <Typography level="body-sm" sx={{ fontWeight: 'md', mb: 1 }}>
                                    Send Test Message
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                                    <FormControl sx={{ flex: 1 }}>
                                        <Input
                                            size="sm"
                                            placeholder="Enter Lead ID"
                                            value={testLeadId}
                                            onChange={(e) => setTestLeadId(e.target.value)}
                                            type="number"
                                        />
                                    </FormControl>
                                    <Button
                                        size="sm"
                                        variant="outlined"
                                        color="neutral"
                                        onClick={handleSendTest}
                                        disabled={sendingTest || !message.trim() || !testLeadId.trim()}
                                        loading={sendingTest}
                                    >
                                        Send Test
                                    </Button>
                                </Box>
                                <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.5 }}>
                                    Send a test SMS to a specific lead to preview the message
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                <Button
                                    variant="solid"
                                    color="primary"
                                    size="lg"
                                    startDecorator={sending ? <CircularProgress size="sm" /> : <SendIcon />}
                                    onClick={handleSend}
                                    disabled={sending || !message.trim() || previewCount === 0}
                                >
                                    {sending ? 'Sending...' : `Send to ${previewCount?.toLocaleString() || 0} Recipients`}
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Past Campaigns */}
                    <Card variant="outlined">
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                <Typography level="title-md">
                                    Past Campaigns
                                </Typography>
                                <IconButton
                                    size="sm"
                                    variant="plain"
                                    onClick={loadCampaigns}
                                    disabled={loadingCampaigns}
                                >
                                    <RefreshIcon />
                                </IconButton>
                            </Box>

                            {loadingCampaigns ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                    <CircularProgress />
                                </Box>
                            ) : campaigns.length === 0 ? (
                                <Alert color="neutral" size="sm">
                                    No campaigns sent yet. Create your first campaign above!
                                </Alert>
                            ) : (
                                <Sheet sx={{ overflow: 'auto', borderRadius: 'sm' }}>
                                    <Table
                                        size="sm"
                                        stickyHeader
                                        sx={{
                                            '& thead th': { 
                                                bgcolor: 'background.level1',
                                                fontWeight: 'lg',
                                            },
                                            '& tbody td': {
                                                py: 1,
                                            },
                                        }}
                                    >
                                        <thead>
                                            <tr>
                                                <th style={{ width: 140 }}>Date</th>
                                                <th>Message</th>
                                                <th style={{ width: 80, textAlign: 'center' }}>Sent</th>
                                                <th style={{ width: 80, textAlign: 'center' }}>Failed</th>
                                                <th style={{ width: 100, textAlign: 'center' }}>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {campaigns.map((campaign) => (
                                                <tr key={campaign.id}>
                                                    <td>
                                                        <Typography level="body-xs">
                                                            {formatDate(campaign.created_at)}
                                                        </Typography>
                                                    </td>
                                                    <td>
                                                        <Tooltip 
                                                            title={campaign.message_template} 
                                                            size="sm"
                                                            sx={{ maxWidth: 400 }}
                                                        >
                                                            <Typography 
                                                                level="body-xs" 
                                                                sx={{ 
                                                                    maxWidth: 300,
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap',
                                                                }}
                                                            >
                                                                {campaign.message_template}
                                                            </Typography>
                                                        </Tooltip>
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <Typography level="body-xs" color="success">
                                                            {campaign.sent_count}
                                                        </Typography>
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <Typography 
                                                            level="body-xs" 
                                                            color={campaign.failed_count > 0 ? 'danger' : 'neutral'}
                                                        >
                                                            {campaign.failed_count}
                                                        </Typography>
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <StatusChip status={campaign.status} />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </Sheet>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Confirmation Modal */}
            <Modal open={confirmModalOpen} onClose={() => setConfirmModalOpen(false)}>
                <ModalDialog variant="outlined" role="alertdialog">
                    <ModalClose />
                    <Typography level="h4" sx={{ mb: 1 }}>
                        Confirm Send
                    </Typography>
                    <Typography level="body-md" sx={{ mb: 2 }}>
                        You are about to send an SMS to <strong>{previewCount?.toLocaleString()}</strong> recipients.
                    </Typography>
                    <Typography level="body-sm" sx={{ mb: 2, p: 2, bgcolor: 'background.level1', borderRadius: 'sm', fontFamily: 'monospace' }}>
                        {message.length > 200 ? message.substring(0, 200) + '...' : message}
                    </Typography>
                    <Alert color="warning" size="sm" sx={{ mb: 2 }}>
                        This action cannot be undone. Messages will be sent immediately.
                    </Alert>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button variant="plain" color="neutral" onClick={() => setConfirmModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button variant="solid" color="primary" onClick={confirmSend}>
                            Send Campaign
                        </Button>
                    </Box>
                </ModalDialog>
            </Modal>
        </Box>
    );
}
